/**
 * BriefForm — how a customer tells us what to build, without a blank page.
 *
 * Two layers: structured facts (business, pages, contact, style) that the
 * platform turns into the agent's brief, and a free-text description in the
 * customer's own words. Nothing here mentions repos, prompts or models.
 */

import { useState } from 'react';
import { Check, Plus, X } from 'lucide-react';
import type { StudioIntake } from '@/lib/api';
import { Button } from '@/components/ui/button';

export const ARCHETYPES: Array<{
  id: NonNullable<StudioIntake['archetype']>;
  title: string;
  blurb: string;
  pages: string[];
}> = [
  {
    id: 'local-service',
    title: 'Local business or service',
    blurb: 'Salon, clinic, gym, stay, studio — visitors call or WhatsApp you.',
    pages: ['Home', 'Services', 'About', 'Gallery', 'Contact'],
  },
  {
    id: 'ecommerce',
    title: 'Online store',
    blurb: 'Sell products with a cart and checkout.',
    pages: ['Home', 'Shop', 'About', 'Contact', 'FAQ'],
  },
  {
    id: 'b2b-catalogue',
    title: 'Manufacturer or supplier',
    blurb: 'Showcase a catalogue, get enquiries — no online checkout.',
    pages: ['Home', 'Products', 'About', 'Quality', 'Contact'],
  },
  {
    id: 'marketing',
    title: 'Company, SaaS or portfolio',
    blurb: 'Tell your story, capture leads.',
    pages: ['Home', 'Features', 'Pricing', 'About', 'Contact'],
  },
];

const MOODS = [
  'Clean & minimal',
  'Warm & handcrafted',
  'Bold & modern',
  'Luxury & refined',
  'Playful & bright',
];

export interface BriefFormValue {
  intake: StudioIntake;
  prompt: string;
}

export function emptyBrief(seed?: Partial<StudioIntake>): BriefFormValue {
  return { intake: { pages: [], ...seed }, prompt: '' };
}

export function briefIsReady(v: BriefFormValue): boolean {
  return Boolean(v.intake.businessName?.trim()) && v.prompt.trim().length >= 20;
}

/** Sections can be shown one at a time (wizard) or all together (studio). */
export type BriefSection = 'business' | 'website';

export function BriefForm({
  value,
  onChange,
  sections = ['business', 'website'],
  disabled,
}: {
  value: BriefFormValue;
  onChange: (v: BriefFormValue) => void;
  sections?: BriefSection[];
  disabled?: boolean;
}) {
  const { intake, prompt } = value;
  const set = (patch: Partial<StudioIntake>) =>
    onChange({ ...value, intake: { ...intake, ...patch } });
  const setContact = (patch: Partial<NonNullable<StudioIntake['contact']>>) =>
    set({ contact: { ...(intake.contact ?? {}), ...patch } });
  const [newPage, setNewPage] = useState('');

  const field =
    'bg-background w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:opacity-60';
  const label = 'mb-1 block text-sm font-medium';
  const hint = 'text-muted-foreground mt-1 text-xs';

  return (
    <div className="space-y-8">
      {sections.includes('business') && (
        <section className="space-y-5">
          <div>
            <label className={label}>Business name</label>
            <input
              className={field}
              value={intake.businessName ?? ''}
              onChange={(e) => set({ businessName: e.target.value })}
              placeholder="e.g. Woodlark Veneers"
              disabled={disabled}
              maxLength={120}
            />
          </div>
          <div>
            <label className={label}>What kind of website?</label>
            <div className="grid gap-3 sm:grid-cols-2">
              {ARCHETYPES.map((a) => {
                const selected = intake.archetype === a.id;
                return (
                  <button
                    type="button"
                    key={a.id}
                    disabled={disabled}
                    onClick={() =>
                      set({
                        archetype: a.id,
                        pages: intake.pages?.length ? intake.pages : a.pages,
                      })
                    }
                    className={`rounded-lg border p-4 text-left transition-colors ${
                      selected ? 'border-primary bg-primary/5' : 'hover:border-foreground/30'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <p className="font-medium">{a.title}</p>
                      {selected && <Check className="text-primary h-4 w-4" />}
                    </div>
                    <p className="text-muted-foreground mt-1 text-xs">{a.blurb}</p>
                  </button>
                );
              })}
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className={label}>Industry</label>
              <input
                className={field}
                value={intake.industry ?? ''}
                onChange={(e) => set({ industry: e.target.value })}
                placeholder="e.g. Interior materials"
                disabled={disabled}
                maxLength={120}
              />
            </div>
            <div>
              <label className={label}>Where are you based?</label>
              <input
                className={field}
                value={intake.locations ?? ''}
                onChange={(e) => set({ locations: e.target.value })}
                placeholder="City, country — or 'online only'"
                disabled={disabled}
                maxLength={500}
              />
            </div>
          </div>
          <div>
            <label className={label}>What do you offer?</label>
            <textarea
              className={field}
              rows={3}
              value={intake.offerings ?? ''}
              onChange={(e) => set({ offerings: e.target.value })}
              placeholder="Your products or services, in a few lines. Real names and details help."
              disabled={disabled}
              maxLength={2000}
            />
          </div>
          <div>
            <label className={label}>Who is it for?</label>
            <input
              className={field}
              value={intake.audience ?? ''}
              onChange={(e) => set({ audience: e.target.value })}
              placeholder="e.g. architects and interior designers in India"
              disabled={disabled}
              maxLength={1000}
            />
          </div>
          <div>
            <p className={label}>Contact details visitors should see</p>
            <p className={`${hint} mb-2`}>
              We only ever show details you give us. Anything you leave blank is left blank on the
              site — never made up.
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              <input
                className={field}
                placeholder="Phone"
                value={intake.contact?.phone ?? ''}
                onChange={(e) => setContact({ phone: e.target.value })}
                disabled={disabled}
                maxLength={40}
              />
              <input
                className={field}
                placeholder="WhatsApp number"
                value={intake.contact?.whatsapp ?? ''}
                onChange={(e) => setContact({ whatsapp: e.target.value })}
                disabled={disabled}
                maxLength={40}
              />
              <input
                className={field}
                placeholder="Email"
                value={intake.contact?.email ?? ''}
                onChange={(e) => setContact({ email: e.target.value })}
                disabled={disabled}
                maxLength={120}
              />
              <input
                className={field}
                placeholder="Opening hours"
                value={intake.contact?.hours ?? ''}
                onChange={(e) => setContact({ hours: e.target.value })}
                disabled={disabled}
                maxLength={200}
              />
              <input
                className={`${field} sm:col-span-2`}
                placeholder="Address"
                value={intake.contact?.address ?? ''}
                onChange={(e) => setContact({ address: e.target.value })}
                disabled={disabled}
                maxLength={400}
              />
            </div>
          </div>
          <div>
            <label className={label}>Existing website (optional)</label>
            <input
              className={field}
              value={intake.existingWebsite ?? ''}
              onChange={(e) => set({ existingWebsite: e.target.value })}
              placeholder="https://…  — we'll use it to understand your business"
              disabled={disabled}
            />
          </div>
        </section>
      )}

      {sections.includes('website') && (
        <section className="space-y-5">
          <div>
            <label className={label}>Describe the website you want</label>
            <textarea
              className={field}
              rows={6}
              value={prompt}
              onChange={(e) => onChange({ ...value, prompt: e.target.value })}
              placeholder="Tell us in your own words. What should visitors feel? What's the one thing they should do? Any sections you definitely want?"
              disabled={disabled}
              maxLength={6000}
            />
            <p className={hint}>
              {prompt.trim().length < 20
                ? 'A couple of sentences is enough to start.'
                : `${prompt.length} characters`}
            </p>
          </div>
          <div>
            <label className={label}>Pages</label>
            <div className="flex flex-wrap gap-2">
              {(intake.pages ?? []).map((p) => (
                <span
                  key={p}
                  className="bg-muted inline-flex items-center gap-1 rounded-full px-3 py-1 text-sm"
                >
                  {p}
                  {!disabled && (
                    <button
                      type="button"
                      onClick={() => set({ pages: (intake.pages ?? []).filter((x) => x !== p) })}
                      aria-label={`Remove ${p}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </span>
              ))}
              <span className="inline-flex items-center gap-1">
                <input
                  className="bg-background w-32 rounded-full border px-3 py-1 text-sm"
                  placeholder="Add a page"
                  value={newPage}
                  disabled={disabled}
                  onChange={(e) => setNewPage(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && newPage.trim()) {
                      e.preventDefault();
                      set({
                        pages: Array.from(new Set([...(intake.pages ?? []), newPage.trim()])).slice(
                          0,
                          20
                        ),
                      });
                      setNewPage('');
                    }
                  }}
                />
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  disabled={disabled || !newPage.trim()}
                  onClick={() => {
                    set({
                      pages: Array.from(new Set([...(intake.pages ?? []), newPage.trim()])).slice(
                        0,
                        20
                      ),
                    });
                    setNewPage('');
                  }}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </span>
            </div>
          </div>
          <div>
            <label className={label}>Look and feel</label>
            <div className="flex flex-wrap gap-2">
              {MOODS.map((m) => (
                <button
                  type="button"
                  key={m}
                  disabled={disabled}
                  onClick={() =>
                    set({
                      style: {
                        ...(intake.style ?? {}),
                        mood: intake.style?.mood === m ? undefined : m,
                      },
                    })
                  }
                  className={`rounded-full border px-3 py-1 text-sm transition-colors ${intake.style?.mood === m ? 'border-primary bg-primary/10 text-primary' : 'hover:bg-muted'}`}
                >
                  {m}
                </button>
              ))}
            </div>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <input
                className={field}
                placeholder="Colours you love or must use (optional)"
                value={intake.style?.colors ?? ''}
                onChange={(e) =>
                  set({ style: { ...(intake.style ?? {}), colors: e.target.value } })
                }
                disabled={disabled}
                maxLength={200}
              />
              <input
                className={field}
                placeholder="Tagline (optional)"
                value={intake.tagline ?? ''}
                onChange={(e) => set({ tagline: e.target.value })}
                disabled={disabled}
                maxLength={200}
              />
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className={label}>Must-haves</label>
              <textarea
                className={field}
                rows={2}
                value={intake.mustHaves ?? ''}
                onChange={(e) => set({ mustHaves: e.target.value })}
                placeholder="e.g. WhatsApp button on every page, Google Maps on contact"
                disabled={disabled}
                maxLength={2000}
              />
            </div>
            <div>
              <label className={label}>Please avoid</label>
              <textarea
                className={field}
                rows={2}
                value={intake.avoid ?? ''}
                onChange={(e) => set({ avoid: e.target.value })}
                placeholder="e.g. stock photos of handshakes, dark backgrounds"
                disabled={disabled}
                maxLength={1000}
              />
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
